import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { sitesApi } from './sitesApi';
import {
  defaultOwnershipEmail,
  domainReady,
  domainStatusLabel,
  ownerFacingPublishError,
  ownershipVerified,
} from './publishPanelUtils';

function mergePublishedVersion(prev, versionNum) {
  const n = Number(versionNum);
  if (!Number.isFinite(n) || n <= 0) return prev;
  if (prev.some((v) => Number(v.version ?? v.id) === n)) return prev;
  return [...prev, { id: String(n), version: n, published: true }].sort(
    (a, b) => Number(a.version ?? a.id) - Number(b.version ?? b.id),
  );
}

function copyText(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return Promise.reject(new Error('clipboard unavailable'));
}

export default function PublishPanel({
  open,
  onClose,
  workspaceId,
  siteId,
  brief,
  allFactsConfirmed,
  ownership,
  domain,
  published,
  versions = [],
  onSiteUpdated,
  onVersionsChange,
  onPublishSuccess,
}) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [host, setHost] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [liveUrl, setLiveUrl] = useState('');
  const [unpublished, setUnpublished] = useState(false);
  const [domainLive, setDomainLive] = useState(null);
  const publishBtnRef = useRef(null);
  const wasOpenRef = useRef(false);

  const verified = ownershipVerified(ownership);
  const effectiveDomain = domainLive || domain;
  const domainActive = domainReady(effectiveDomain);
  const defaultEmail = useMemo(() => defaultOwnershipEmail(brief), [brief]);

  useEffect(() => {
    if (domain?.host) setHost(domain.host);
  }, [domain?.host]);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setEmail(defaultEmail);
    setHost(domain?.host || '');
    setLiveUrl(published?.host ? `https://${published.host}/` : '');
    setUnpublished(!published?.host && versions.length > 0);
    setError('');
    setCodeSent(ownershipVerified(ownership));
    if (domain?.status !== 'active') setDomainLive(null);
  }, [open, defaultEmail, domain?.host, domain?.status, ownership, published?.host, versions.length]);

  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail);
  }, [open, defaultEmail]);

  useEffect(() => {
    if (published?.host && !unpublished) setLiveUrl(`https://${published.host}/`);
  }, [published?.host, unpublished]);

  useEffect(() => {
    if (domain?.status === 'active' && domain?.host) {
      setDomainLive((prev) => {
        if (prev?.host === domain.host && prev?.status === 'active') return prev;
        return {
          host: domain.host,
          status: 'active',
          cname: domain.cname || 'sites.gocta.ai',
        };
      });
    }
  }, [domain?.status, domain?.host, domain?.cname]);

  useEffect(() => {
    if (!open || !domainActive || !publishBtnRef.current) return;
    publishBtnRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
  }, [open, domainActive]);

  async function afterMutation() {
    if (!onSiteUpdated) return true;
    const ok = await onSiteUpdated();
    if (ok === false) {
      setError('Could not refresh site status. Try again.');
    }
    return ok !== false;
  }

  if (!open) return null;

  async function sendCode() {
    setError('');
    setBusy('send');
    try {
      await sitesApi.sendOwnershipCode(workspaceId, siteId, email);
      setCodeSent(true);
      await afterMutation();
    } catch (e) {
      setError(ownerFacingPublishError(e.message) || 'Could not send the code.');
    } finally {
      setBusy('');
    }
  }

  async function verifyCode() {
    setError('');
    setBusy('verify');
    try {
      await sitesApi.verifyOwnershipCode(workspaceId, siteId, code);
      setCode('');
      await afterMutation();
    } catch (e) {
      setError(ownerFacingPublishError(e.message) || 'That code did not match.');
    } finally {
      setBusy('');
    }
  }

  async function connectDomain() {
    setError('');
    setBusy('connect');
    try {
      await sitesApi.connectDomain(workspaceId, siteId, host);
      setDomainLive({ host: host.trim().toLowerCase(), status: 'pending_dns', cname: 'sites.gocta.ai' });
      await afterMutation();
    } catch (e) {
      setError(ownerFacingPublishError(e.message) || 'Could not save that hostname.');
    } finally {
      setBusy('');
    }
  }

  async function checkDomain() {
    setError('');
    setBusy('check');
    try {
      const res = await sitesApi.checkDomain(workspaceId, siteId);
      setDomainLive({
        host: res.host || effectiveDomain?.host || host.trim().toLowerCase(),
        status: res.status || effectiveDomain?.status,
        cname: res.cname || 'sites.gocta.ai',
      });
      await afterMutation();
    } catch (e) {
      setError(ownerFacingPublishError(e.message) || 'Could not check DNS yet.');
    } finally {
      setBusy('');
    }
  }

  async function publishSite() {
    setError('');
    setBusy('publish');
    setUnpublished(false);
    try {
      const res = await sitesApi.publishSite(workspaceId, siteId);
      if (res?.url) setLiveUrl(res.url);
      if (res?.version != null && onVersionsChange) {
        onVersionsChange((prev) => mergePublishedVersion(prev, res.version));
      }
      if (onPublishSuccess) onPublishSuccess(res);
      await afterMutation();
    } catch (e) {
      setError(ownerFacingPublishError(e.message) || 'Publish failed.');
    } finally {
      setBusy('');
    }
  }

  async function rollbackTo(versionNum) {
    setError('');
    setBusy('rollback');
    try {
      const res = await sitesApi.publishSite(workspaceId, siteId, { rollbackTo: versionNum });
      if (res?.url) setLiveUrl(res.url);
      await afterMutation();
    } catch (e) {
      setError(ownerFacingPublishError(e.message) || 'Roll back failed.');
    } finally {
      setBusy('');
    }
  }

  async function unpublishSite() {
    setError('');
    setBusy('unpublish');
    try {
      await sitesApi.publishSite(workspaceId, siteId, { unpublish: true });
      setLiveUrl('');
      setUnpublished(true);
      await afterMutation();
    } catch (e) {
      setError(ownerFacingPublishError(e.message) || 'Unpublish failed.');
    } finally {
      setBusy('');
    }
  }

  const cnameTarget = effectiveDomain?.cname || 'sites.gocta.ai';
  const showRecords = Boolean(effectiveDomain?.host);
  const canPublish = allFactsConfirmed && verified && domainActive;
  const publishDisabledReason = !allFactsConfirmed
    ? 'Confirm every fact in the editor first'
    : !verified
      ? 'Prove ownership with the email code'
      : !domainActive
        ? 'Connect your domain and wait until DNS is active'
        : '';
  const displayLiveUrl =
    liveUrl || (published?.host && !unpublished ? `https://${published.host}/` : '');

  const panel = (
    <div
      className="sites-publish-overlay"
      role="dialog"
      aria-label="Publish your site"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17, 24, 39, 0.45)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sites-publish-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          maxWidth: 520,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '1.25rem 1.5rem',
          boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.125rem' }}>Publish your site</h2>
          <button type="button" className="inbox-pill-btn" onClick={onClose}>
            Close
          </button>
        </div>

        {error && (
          <div className="fb-callout warn" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <section style={{ marginBottom: '1.5rem' }} data-publish-step="1">
          <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>1. Prove it&apos;s yours</h3>
          <p style={{ fontSize: '0.85rem', color: '#4b5563', margin: '0 0 0.75rem' }}>
            We email a 6-digit code to an address on your business domain so only you can publish.
          </p>
          {verified ? (
            <p style={{ color: '#047857', fontWeight: 600, margin: 0 }}>
              Owner confirmed. {ownership.verifiedEmail ? `Verified as ${ownership.verifiedEmail}.` : 'You own this site.'}
            </p>
          ) : (
            <>
              <label className="domains-field-label" htmlFor="publish-ownership-email">
                Email on your domain
              </label>
              <input
                id="publish-ownership-email"
                type="email"
                className="fb-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: '100%', marginBottom: '0.5rem' }}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  className="inbox-pill-btn"
                  disabled={busy === 'send' || !email.includes('@')}
                  onClick={sendCode}
                >
                  {busy === 'send' ? 'Sending…' : 'Send code'}
                </button>
              </div>
              {codeSent && (
                <>
                  <label className="domains-field-label" htmlFor="publish-ownership-code">
                    Enter the 6-digit code
                  </label>
                  <input
                    id="publish-ownership-code"
                    type="text"
                    inputMode="numeric"
                    name="verificationCode"
                    maxLength={6}
                    placeholder="6-digit code"
                    className="fb-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    style={{ width: '100%', marginBottom: '0.5rem' }}
                  />
                  <button
                    type="button"
                    className="inbox-pill-btn"
                    disabled={busy === 'verify' || code.length < 6}
                    onClick={verifyCode}
                  >
                    {busy === 'verify' ? 'Checking…' : 'Verify code'}
                  </button>
                </>
              )}
            </>
          )}
        </section>

        <section
          style={{ marginBottom: '1.5rem', opacity: verified ? 1 : 0.55 }}
          data-publish-step="2"
          aria-disabled={!verified}
        >
          <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>2. Connect your domain</h3>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
            Don&apos;t have a domain yet? Buying one here is coming soon.
          </p>
          <label className="domains-field-label" htmlFor="publish-domain-host">
            Your hostname
          </label>
          <input
            id="publish-domain-host"
            name="domain"
            type="text"
            className="fb-input"
            placeholder="www.yourbusiness.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            disabled={!verified}
            style={{ width: '100%', marginBottom: '0.5rem' }}
          />
          <button
            type="button"
            className="inbox-pill-btn"
            disabled={!verified || busy === 'connect' || !host.trim()}
            onClick={connectDomain}
            style={{ marginBottom: '0.75rem' }}
          >
            {busy === 'connect' ? 'Saving…' : 'Connect domain'}
          </button>

          {showRecords && (
            <div className="domains-dns-panel">
              <h4 className="domains-dns-step-title" style={{ marginTop: 0 }}>
                DNS record to add
              </h4>
              <table className="domains-dns-table" style={{ width: '100%', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Host</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>CNAME</td>
                    <td>{effectiveDomain.host}</td>
                    <td>{cnameTarget}</td>
                  </tr>
                </tbody>
              </table>
              <div className="domains-dns-copy-row" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input type="text" className="fb-input" readOnly value={effectiveDomain.host} style={{ flex: 1 }} />
                <button
                  type="button"
                  className="inbox-pill-btn"
                  aria-label="Copy host"
                  data-copy="host"
                  onClick={() => copyText(effectiveDomain.host)}
                >
                  Copy
                </button>
              </div>
              <div className="domains-dns-copy-row" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input type="text" className="fb-input" readOnly value={cnameTarget} style={{ flex: 1 }} />
                <button
                  type="button"
                  className="inbox-pill-btn"
                  aria-label="Copy CNAME target"
                  data-copy="cname"
                  onClick={() => copyText(cnameTarget)}
                >
                  Copy
                </button>
              </div>
              <p style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                Status: {domainStatusLabel(effectiveDomain.status)}
              </p>
              <button
                type="button"
                className="inbox-pill-btn"
                disabled={!verified || busy === 'check'}
                onClick={checkDomain}
              >
                {busy === 'check' ? 'Checking…' : 'Check'}
              </button>
            </div>
          )}
        </section>

        <section style={{ opacity: domainActive ? 1 : 0.55 }} data-publish-step="3">
          <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>3. Publish</h3>
          <ul style={{ fontSize: '0.85rem', color: '#374151', paddingLeft: '1.1rem', margin: '0 0 0.75rem' }}>
            <li>{allFactsConfirmed ? 'Every fact confirmed' : 'Confirm every fact first'}</li>
            <li>{verified ? 'Ownership verified' : 'Prove ownership with the code'}</li>
            <li>{domainActive ? `Domain active (${effectiveDomain?.host})` : 'Connect your domain and wait until DNS is active'}</li>
          </ul>

          {displayLiveUrl && (
            <p className="sites-live-url" style={{ fontSize: '0.9rem', margin: '0 0 0.75rem' }}>
              Live URL:{' '}
              <a href={displayLiveUrl} target="_blank" rel="noreferrer">
                {displayLiveUrl}
              </a>
            </p>
          )}

          {unpublished && (
            <p style={{ color: '#6b7280', fontWeight: 600, margin: '0 0 0.75rem' }}>
              This site is not live (offline). R2 versions are kept so you can publish again.
            </p>
          )}

          {versions.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <h4 style={{ fontSize: '0.9rem', margin: '0 0 0.35rem' }}>Versions</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {versions.map((v) => {
                  const n = v.version ?? v.id;
                  return (
                    <li
                      key={String(n)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.35rem 0',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '0.85rem',
                      }}
                    >
                      <span>
                        v{n}
                        {published?.version === Number(n) ? ' (live)' : ''}
                      </span>
                      {published?.host &&
                        Number(n) > 0 &&
                        published?.version !== Number(n) &&
                        domainActive &&
                        !unpublished && (
                        <button
                          type="button"
                          className="inbox-pill-btn"
                          disabled={busy === 'rollback'}
                          onClick={() => rollbackTo(n)}
                        >
                          Roll back
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              ref={publishBtnRef}
              type="button"
              className="inbox-pill-btn"
              style={{ fontWeight: 700 }}
              disabled={busy === 'publish' || !canPublish}
              title={canPublish ? 'Publish this version to your domain' : publishDisabledReason}
              onClick={publishSite}
            >
              {busy === 'publish'
                ? 'Publishing…'
                : published?.host && !unpublished
                  ? 'Publish again'
                  : 'Publish now'}
            </button>
            {published?.host && domainActive && !unpublished && (
              <button
                type="button"
                className="inbox-pill-btn"
                disabled={busy === 'unpublish'}
                onClick={unpublishSite}
              >
                {busy === 'unpublish' ? 'Removing…' : 'Unpublish'}
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
  return createPortal(panel, document.body);
}
